import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer } from 'recharts';
import { FileText, Download, TrendingUp, Package, DollarSign, BarChart3, Eye, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

// Simular dados do marketplace (baseado no seu código)
const generateMockData = () => {
  const produtos = [
    'Smartphone Galaxy A54', 'iPhone 15 Pro', 'Notebook Dell Inspiron', 'Smart TV 55" Samsung',
    'Fone Bluetooth JBL', 'Tablet iPad 10.9', 'Smartwatch Apple Watch', 'Console PlayStation 5',
    'Câmera Canon EOS', 'Headset Gamer Logitech', 'Monitor 27" LG', 'Teclado Mecânico Razer',
    'Mouse Gamer Corsair', 'SSD 1TB Samsung', 'Placa de Vídeo RTX 4060', 'Processador Intel i7',
    'Memória RAM 16GB', 'HD Externo 2TB', 'Impressora HP LaserJet', 'Roteador WiFi 6'
  ];

  return produtos.map((nome, index) => {
    const vendas = Math.floor(Math.random() * 2000) + 50;
    const preco = Math.floor(Math.random() * 3000) + 100;
    const receita = vendas * preco;
    const estoque = Math.floor(Math.random() * 100) + 10;
    const health = Math.random();
    
    return {
      id: `MLB${1000000 + index}`,
      nome,
      vendas,
      preco,
      receita,
      estoque,
      health,
      categoria: ['Eletrônicos', 'Informática', 'Celulares', 'Games'][Math.floor(Math.random() * 4)],
      margem: Math.random() * 0.3 + 0.1, // 10% a 40%
      status: ['active', 'paused'][Math.floor(Math.random() * 2)]
    };
  }).sort((a, b) => b.receita - a.receita);
};

const CurvaABCPDFGenerator = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    // Simular carregamento de dados
    setTimeout(() => {
      setDados(generateMockData());
      setLoading(false);
    }, 1500);
  }, [selectedPeriod]);

  // Calcular Curva ABC
  const dadosABC = useMemo(() => {
    if (!dados.length) return [];

    const receitaTotal = dados.reduce((sum, item) => sum + item.receita, 0);
    let acumulado = 0;

    return dados.map((item, index) => {
      acumulado += item.receita;
      const percentualAcumulado = (acumulado / receitaTotal) * 100;
      
      let classe = 'C';
      if (percentualAcumulado <= 80) classe = 'A';
      else if (percentualAcumulado <= 95) classe = 'B';

      return {
        ...item,
        posicao: index + 1,
        percentualReceita: (item.receita / receitaTotal) * 100,
        percentualAcumulado,
        classe
      };
    });
  }, [dados]);

  // Estatísticas por classe
  const estatisticasABC = useMemo(() => {
    const stats = { A: [], B: [], C: [] };
    dadosABC.forEach(item => stats[item.classe].push(item));

    return Object.keys(stats).map(classe => {
      const items = stats[classe];
      const totalReceita = items.reduce((sum, item) => sum + item.receita, 0);
      const totalVendas = items.reduce((sum, item) => sum + item.vendas, 0);
      const receitaTotal = dadosABC.reduce((sum, item) => sum + item.receita, 0);

      return {
        classe,
        quantidade: items.length,
        percentualItens: (items.length / dadosABC.length) * 100,
        receita: totalReceita,
        percentualReceita: (totalReceita / receitaTotal) * 100,
        vendas: totalVendas,
        ticketMedio: totalReceita / totalVendas || 0
      };
    });
  }, [dadosABC]);

  // Dados para gráficos
  const dadosGraficoBarras = dadosABC.slice(0, 10).map(item => ({
    nome: item.nome.length > 20 ? item.nome.substring(0, 20) + '...' : item.nome,
    receita: item.receita,
    vendas: item.vendas,
    classe: item.classe
  }));

  const dadosGraficoPizza = estatisticasABC.map(stat => ({
    name: `Classe ${stat.classe}`,
    value: stat.percentualReceita,
    quantidade: stat.quantidade
  }));

  const formatCurrency = (value) => 
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatNumber = (value) => 
    new Intl.NumberFormat('pt-BR').format(value);

  const getClassColor = (classe) => {
    switch (classe) {
      case 'A': return '#28a745';
      case 'B': return '#ffc107';
      case 'C': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const generatePDF = () => {
    const printContent = document.getElementById('pdf-content');
    const originalContent = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="container-fluid py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <h5 className="text-muted">Processando dados para Curva ABC...</h5>
            <p className="text-muted">Analisando performance dos produtos</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h2 className="fw-bold mb-1 text-dark d-flex align-items-center">
                <BarChart3 className="me-3 text-primary" size={32} />
                Curva ABC - Análise de Performance
              </h2>
              <p className="text-muted mb-0">Análise estratégica dos produtos por faturamento</p>
            </div>

            <div className="d-flex align-items-center gap-3">
              <select 
                className="form-select"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                style={{ minWidth: '150px' }}
              >
                <option value="7d">Últimos 7 dias</option>
                <option value="30d">Últimos 30 dias</option>
                <option value="90d">Últimos 90 dias</option>
                <option value="1y">Último ano</option>
              </select>

              <button 
                className="btn btn-outline-primary"
                onClick={() => setShowPreview(!showPreview)}
              >
                <Eye className="me-2" size={16} />
                {showPreview ? 'Ocultar' : 'Visualizar'} Preview
              </button>

              <button 
                className="btn btn-primary px-4"
                onClick={generatePDF}
              >
                <Download className="me-2" size={16} />
                Gerar PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="row mb-4">
        {estatisticasABC.map((stat, index) => (
          <div key={stat.classe} className="col-md-4 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="card-title mb-0 d-flex align-items-center">
                    <div 
                      className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                      style={{ 
                        width: '40px', 
                        height: '40px', 
                        backgroundColor: getClassColor(stat.classe) + '20',
                        color: getClassColor(stat.classe)
                      }}
                    >
                      <span className="fw-bold">{stat.classe}</span>
                    </div>
                    Classe {stat.classe}
                  </h5>
                  <span 
                    className="badge px-3 py-2"
                    style={{ backgroundColor: getClassColor(stat.classe), color: 'white' }}
                  >
                    {stat.percentualReceita.toFixed(1)}%
                  </span>
                </div>
                
                <div className="row g-3">
                  <div className="col-6">
                    <div className="text-center">
                      <h6 className="mb-0 fw-bold">{stat.quantidade}</h6>
                      <small className="text-muted">Produtos</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center">
                      <h6 className="mb-0 fw-bold">{formatCurrency(stat.receita)}</h6>
                      <small className="text-muted">Faturamento</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center">
                      <h6 className="mb-0 fw-bold">{formatNumber(stat.vendas)}</h6>
                      <small className="text-muted">Vendas</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center">
                      <h6 className="mb-0 fw-bold">{formatCurrency(stat.ticketMedio)}</h6>
                      <small className="text-muted">Ticket Médio</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Gráficos */}
      <div className="row mb-4">
        <div className="col-lg-8 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0 py-3">
              <h6 className="card-title mb-0 fw-semibold">Top 10 Produtos por Faturamento</h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dadosGraficoBarras}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="nome" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    fontSize={12}
                  />
                  <YAxis 
                    yAxisId="receita"
                    orientation="left"
                    tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}K`}
                    fontSize={12}
                  />
                  <YAxis 
                    yAxisId="vendas"
                    orientation="right"
                    fontSize={12}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'receita' ? formatCurrency(value) : formatNumber(value),
                      name === 'receita' ? 'Faturamento' : 'Vendas'
                    ]}
                    labelStyle={{ color: '#333' }}
                  />
                  <Bar 
                    yAxisId="receita"
                    dataKey="receita" 
                    fill="#3b82f6"
                    name="receita"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="col-lg-4 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0 py-3">
              <h6 className="card-title mb-0 fw-semibold">Distribuição por Classe ABC</h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dadosGraficoPizza}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({name, value}) => `${name}: ${value.toFixed(1)}%`}
                  >
                    {dadosGraficoPizza.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getClassColor(['A', 'B', 'C'][index])} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`${value.toFixed(1)}%`, 'Participação']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Produtos */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white border-0 py-3">
          <h6 className="card-title mb-0 fw-semibold">Ranking de Produtos - Curva ABC</h6>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="border-0 fw-semibold py-3">#</th>
                  <th className="border-0 fw-semibold py-3">Produto</th>
                  <th className="border-0 fw-semibold py-3">Classe</th>
                  <th className="border-0 fw-semibold py-3">Faturamento</th>
                  <th className="border-0 fw-semibold py-3">% Receita</th>
                  <th className="border-0 fw-semibold py-3">% Acumulado</th>
                  <th className="border-0 fw-semibold py-3">Vendas</th>
                  <th className="border-0 fw-semibold py-3">Preço Médio</th>
                  <th className="border-0 fw-semibold py-3">Estoque</th>
                  <th className="border-0 fw-semibold py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {dadosABC.slice(0, 15).map((produto) => (
                  <tr key={produto.id} className="align-middle">
                    <td className="fw-semibold">{produto.posicao}</td>
                    <td>
                      <div>
                        <h6 className="mb-1 fw-semibold">{produto.nome}</h6>
                        <small className="text-muted">{produto.id}</small>
                      </div>
                    </td>
                    <td>
                      <span 
                        className="badge px-3 py-2 fw-semibold"
                        style={{ 
                          backgroundColor: getClassColor(produto.classe) + '20',
                          color: getClassColor(produto.classe),
                          border: `1px solid ${getClassColor(produto.classe)}`
                        }}
                      >
                        {produto.classe}
                      </span>
                    </td>
                    <td className="fw-semibold">{formatCurrency(produto.receita)}</td>
                    <td>{produto.percentualReceita.toFixed(2)}%</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="progress me-2" style={{ width: '60px', height: '6px' }}>
                          <div 
                            className="progress-bar" 
                            style={{ 
                              width: `${produto.percentualAcumulado}%`,
                              backgroundColor: getClassColor(produto.classe)
                            }}
                          />
                        </div>
                        <small className="text-muted">{produto.percentualAcumulado.toFixed(1)}%</small>
                      </div>
                    </td>
                    <td>{formatNumber(produto.vendas)}</td>
                    <td>{formatCurrency(produto.preco)}</td>
                    <td>
                      <span className={`fw-semibold ${
                        produto.estoque < 10 ? 'text-danger' : 
                        produto.estoque < 30 ? 'text-warning' : 'text-success'
                      }`}>
                        {produto.estoque}
                      </span>
                    </td>
                    <td>
                      {produto.status === 'active' ? (
                        <CheckCircle className="text-success" size={20} />
                      ) : (
                        <XCircle className="text-danger" size={20} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Preview do PDF */}
      {showPreview && (
        <div className="card border-0 shadow-lg mb-4">
          <div className="card-header bg-primary text-white py-3">
            <h6 className="card-title mb-0 fw-semibold text-white">
              <FileText className="me-2" size={20} />
              Preview do Relatório PDF
            </h6>
          </div>
          <div className="card-body p-4" style={{ backgroundColor: '#fff' }}>
            <div id="pdf-content" className="pdf-content">
              {/* Cabeçalho do PDF */}
              <div className="text-center mb-5 border-bottom pb-4">
                <h1 className="fw-bold text-primary mb-2">RELATÓRIO CURVA ABC</h1>
                <h2 className="h4 text-muted mb-3">Análise de Performance - Marketplace</h2>
                <div className="row text-center">
                  <div className="col-md-4">
                    <strong>Período:</strong> {selectedPeriod === '30d' ? 'Últimos 30 dias' : selectedPeriod}
                  </div>
                  <div className="col-md-4">
                    <strong>Data:</strong> {new Date().toLocaleDateString('pt-BR')}
                  </div>
                  <div className="col-md-4">
                    <strong>Total de Produtos:</strong> {dadosABC.length}
                  </div>
                </div>
              </div>

              {/* Resumo Executivo */}
              <div className="mb-5">
                <h3 className="fw-bold mb-4 text-primary">📊 Resumo Executivo</h3>
                <div className="row">
                  {estatisticasABC.map((stat) => (
                    <div key={stat.classe} className="col-md-4 mb-3">
                      <div className="border rounded p-3 h-100" style={{ borderColor: getClassColor(stat.classe) }}>
                        <h4 className="fw-bold mb-3" style={{ color: getClassColor(stat.classe) }}>
                          Classe {stat.classe}
                        </h4>
                        <p><strong>Produtos:</strong> {stat.quantidade} ({stat.percentualItens.toFixed(1)}%)</p>
                        <p><strong>Faturamento:</strong> {formatCurrency(stat.receita)} ({stat.percentualReceita.toFixed(1)}%)</p>
                        <p><strong>Vendas:</strong> {formatNumber(stat.vendas)}</p>
                        <p className="mb-0"><strong>Ticket Médio:</strong> {formatCurrency(stat.ticketMedio)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Análise Detalhada */}
              <div className="mb-5">
                <h3 className="fw-bold mb-4 text-primary">📈 Análise Detalhada</h3>
                <div className="table-responsive">
                  <table className="table table-striped table-bordered">
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th>#</th>
                        <th>Produto</th>
                        <th>Classe</th>
                        <th>Faturamento</th>
                        <th>% Receita</th>
                        <th>% Acumulado</th>
                        <th>Vendas</th>
                        <th>Preço Médio</th>
                        <th>Estoque</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dadosABC.map((produto) => (
                        <tr key={produto.id}>
                          <td className="fw-bold">{produto.posicao}</td>
                          <td>
                            <div>
                              <strong>{produto.nome}</strong><br />
                              <small className="text-muted">{produto.id}</small>
                            </div>
                          </td>
                          <td>
                            <span 
                              className="badge px-2 py-1"
                              style={{ 
                                backgroundColor: getClassColor(produto.classe),
                                color: 'white'
                              }}
                            >
                              {produto.classe}
                            </span>
                          </td>
                          <td className="fw-semibold">{formatCurrency(produto.receita)}</td>
                          <td>{produto.percentualReceita.toFixed(2)}%</td>
                          <td>{produto.percentualAcumulado.toFixed(1)}%</td>
                          <td>{formatNumber(produto.vendas)}</td>
                          <td>{formatCurrency(produto.preco)}</td>
                          <td className={
                            produto.estoque < 10 ? 'text-danger fw-bold' : 
                            produto.estoque < 30 ? 'text-warning fw-bold' : 'text-success fw-bold'
                          }>
                            {produto.estoque}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Insights e Recomendações */}
              <div className="mb-5">
                <h3 className="fw-bold mb-4 text-primary">💡 Insights e Recomendações</h3>
                <div className="row">
                  <div className="col-md-6">
                    <div className="card border-success">
                      <div className="card-header bg-success text-white">
                        <h5 className="mb-0">✅ Pontos Fortes</h5>
                      </div>
                      <div className="card-body">
                        <ul className="list-unstyled">
                          <li className="mb-2">• Classe A representa {estatisticasABC[0]?.percentualReceita.toFixed(1)}% do faturamento</li>
                          <li className="mb-2">• Top 3 produtos concentram alta receita</li>
                          <li className="mb-2">• Diversificação adequada de categorias</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-warning">
                      <div className="card-header bg-warning text-dark">
                        <h5 className="mb-0">⚠️ Pontos de Atenção</h5>
                      </div>
                      <div className="card-body">
                        <ul className="list-unstyled">
                          <li className="mb-2">• Monitorar estoque dos produtos Classe A</li>
                          <li className="mb-2">• Avaliar descontinuação de produtos Classe C</li>
                          <li className="mb-2">• Otimizar margem dos produtos Classe B</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rodapé */}
              <div className="text-center mt-5 pt-4 border-top">
                <p className="text-muted mb-0">
                  Relatório gerado automaticamente em {new Date().toLocaleString('pt-BR')}
                </p>
                <p className="text-muted small">
                  Sistema de Gestão Marketplace - Curva ABC Analytics
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .pdf-content, .pdf-content * {
            visibility: visible;
          }
          .pdf-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .card {
            border: none !important;
            box-shadow: none !important;
          }
          .table {
            font-size: 12px;
          }
          .badge {
            color: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CurvaABCPDFGenerator;